import { inventories, mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  DefaultApiWmsOutgoingPostRequest,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  WorkLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface ExtendedFormValues
  extends Partial<WorkLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner> {
  locationCodes: Record<number, string>; // ID별 locationCode
  quantities: Record<number, string>; // ID별 quantity
}

export const usePruchaseOrderOutgingLogic = (
  formatterProps:
    | AuthSignupPost201ResponseEquipmentPurchaseOrderItem[]
    // | AuthSignupPost201ResponseEquipmentPurchaseOrderItem
    | undefined,
  selectLots: { [key: number]: any },
) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const [selectLot, setSelectLot] =
    useState<ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner>();

  const { mutateAsync: outgoingMutate } = useMutation(
    (params: DefaultApiWmsOutgoingPostRequest) =>
      mutateInventories
        .outgoing(params)
        .mutationFn(params as DefaultApiWmsOutgoingPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        queryClient.invalidateQueries(["purchaseOrders"]);
        customAlert(t("delivery.successMessage"), t("delivery.success"), "green");
        closeModal(null);
      },
      onError: () => {
        customAlert(t("delivery.failMessage"), t("delivery.fail"), "red");
      },
    },
  );

  const inventoryQueries = useQueries({
    queries: formatterProps
      ? formatterProps.map((item) => {
          const { queryKey, queryFn } = inventories.find({
            query: {
              $and: [{ itemCode: { $eq: item.itemCode } }],
            },
            populate: ["lot", "location", "item"],
          });
          return {
            queryKey,
            queryFn,
            enabled: !!item.itemCode,
          };
        })
      : [],
  });

  const inventoryData = inventoryQueries?.map((query) => query.data).filter(Boolean);

  const form = useForm<ExtendedFormValues>({
    initialValues: {
      itemCode: undefined,
      locationCodes: {},
      quantities: {},
      lotId: null,
      lotInfo: {
        name: null,
        expiration: null,
      },
      purchaseOrderItemId: undefined,
      ...(customFunctions.ADD_PURCHASE_ORDER_INVOICE_DATE
        ? { invoiceDate: new Date().toISOString(), invoiceRefNo: undefined }
        : {}),
    },
  });

  const onSubmit = async () => {
    try {
      await Promise.all(
        formatterProps?.map((item) =>
          outgoingMutate({
            workLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner: {
              itemCode: item?.itemCode ?? "",
              locationCode:
                form.values.locationCodes?.[item.id ?? 0] ?? selectLots[item.id ?? 0]?.locationCode,
              quantity:
                form.values.quantities?.[item.id ?? 0] ?? selectLots[item.id ?? 0]?.quantity,
              lotId: Number(selectLots[item.id ?? 0]?.lotId || form.values.lotId),
              lotInfo: {
                name: selectLots[item.id ?? 0]?.lot?.name,
                expiration: selectLots[item.id ?? 0]?.lot?.expiration,
              },
              purchaseOrderItemId: item?.id,
              actionFrom: "WEB",
              ...(customFunctions.ADD_PURCHASE_ORDER_INVOICE_DATE
                ? { invoiceDate: form.values.invoiceDate, invoiceRefNo: undefined }
                : {}),
            },
          }),
        ) ?? [],
      );
    } catch (e) {
      customAlert(t("delivery.failMessage"), t("delivery.fail"), "red");
    }
  };

  return {
    inventoryData,
    form,
    onSubmit,
    setSelectLot,
    selectLot,
  };
};
